import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight } from 'lucide-react';
import StaticPageLayout from '../../../components/StaticPageLayout';
import { useThemeStore } from '../../../store/theme';

const tableOfContents = [
  { id: 'what-plans-do-you-offer', title: 'What plans do you offer?' },
  { id: 'billing-periods', title: 'Billing periods (how you\'re charged)' },
  { id: 'current-pricing', title: 'Current pricing' },
  { id: 'how-credits-work', title: 'How credits work' },
  { id: 'money-back-guarantee', title: 'Money-back guarantee (first 14 days)' },
  { id: 'more-information', title: 'More Information' },
];

const relatedArticles = [
  { title: 'How credits work in detail', href: '/doc/article/how-credits-work' },
  { title: 'How to upgrade or downgrade your plan', href: '/doc/article/upgrade-downgrade-plan' },
  { title: 'Understanding your billing cycle', href: '/doc/article/billing-cycle' },
  { title: 'Refund policy explained', href: '/refund-policy' },
  { title: 'Enterprise plans and custom pricing', href: '/doc/article/enterprise-plans' },
];

export default function PlansAndPricing() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeSection, setActiveSection] = useState('');
  const [isAtTop, setIsAtTop] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      const sections = tableOfContents.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 100;

      // Check if at top of page (before first section)
      const firstSection = sections[0];
      if (firstSection && scrollPosition < firstSection.offsetTop) {
        setIsAtTop(true);
        setActiveSection('');
        return;
      }

      setIsAtTop(false);

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(tableOfContents[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  // SEO Schema markup
  const schemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'SweetPilot Plans and Pricing - AI Employee Platform Subscription',
    description: 'Complete guide to SweetPilot pricing plans, billing periods, credits system, and money-back guarantee. Find the right plan for your business.',
    author: { '@type': 'Organization', name: 'SweetPilot' },
    publisher: { '@type': 'Organization', name: 'SweetPilot', logo: { '@type': 'ImageObject', url: 'https://sweetpilot.com/logo.png' } },
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://sweetpilot.com/doc/article/pricing-and-plan' }
  };

  return (
    <>
      <Helmet>
        <title>SweetPilot Plans and Pricing - Subscription Plans & Credits | SweetPilot Help Center</title>
        <meta name="description" content="Explore SweetPilot pricing plans: Starter, Professional, and Enterprise. Learn about billing periods, how credits work, and our 14-day money-back guarantee." />
        <meta name="keywords" content="SweetPilot pricing, SweetPilot plans, AI employee pricing, SweetPilot subscription, SweetPilot credits, AI platform cost, business automation pricing, SweetPilot enterprise" />
        <meta property="og:title" content="SweetPilot Plans and Pricing - Find Your Perfect Plan" />
        <meta property="og:description" content="Compare SweetPilot subscription plans. Starter, Professional, and Enterprise options with flexible billing and credits system." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://sweetpilot.com/doc/article/pricing-and-plan" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SweetPilot Plans and Pricing" />
        <meta name="twitter:description" content="Find the right SweetPilot plan for your business. Compare features and pricing." />
        <link rel="canonical" href="https://sweetpilot.com/doc/article/pricing-and-plan" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      <StaticPageLayout
        title=""
        subtitle=""
        showBackButton={false}
      >
        <div className="flex gap-16 lg:gap-20">
          {/* Main Content */}
          <div className="flex-1 max-w-2xl">
            {/* Breadcrumbs */}
            <nav className="mb-8">
            <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
              <li>
                <Link
                  to="/docs"
                  className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
                >
                  All Collections
                </Link>
              </li>
              <li>
                <ChevronRight className="w-4 h-4" />
              </li>
              <li>
                <Link
                  to="/doc/collections/getting-started"
                  className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
                >
                  Getting started
                </Link>
              </li>
              <li>
                <ChevronRight className="w-4 h-4" />
              </li>
              <li className={isDark ? 'text-white' : 'text-slate-900'}>
                Plans and pricing
              </li>
            </ol>
          </nav>

          {/* Article Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <h1 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Plans and pricing
            </h1>
            <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              Everything you need to know about SweetPilot's pricing plans, billing, and credits.
            </p>
          </motion.div>

          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}
          >
            <p className={`text-lg leading-relaxed mb-8 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              SweetPilot offers flexible pricing plans designed to fit businesses of all sizes. Whether you're just getting started or running an enterprise operation, we have a plan that works for you. All plans come with access to our full suite of{' '}
              <Link to="/employees" className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}>
                AI Employees
              </Link>{' '}
              and include a <strong>14-day money-back guarantee</strong>.
            </p>

            {/* What plans do you offer? */}
            <h2 id="what-plans-do-you-offer" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              What plans do you offer?
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              SweetPilot offers <strong>four pricing tiers</strong> to accommodate different needs:
            </p>

            {/* Free Plan */}
            <h3 className={`text-xl font-semibold mt-8 mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Free Plan - $0/month
            </h3>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Perfect for individuals who want to <strong>explore SweetPilot</strong> before committing. The Free plan includes:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li><strong>100 credits per month</strong> - enough to test our AI Employees</li>
              <li>Access to <strong>all 15 AI Employees</strong></li>
              <li><strong>Basic integrations</strong> (limited platforms)</li>
              <li><strong>Community support</strong> through our help center</li>
            </ul>

            {/* Pro Plan */}
            <h3 className={`text-xl font-semibold mt-8 mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Pro Plan - $25/month
            </h3>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Ideal for <strong>freelancers and small teams</strong> who need more power. The Pro plan includes:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li><strong>1,500 credits per month</strong></li>
              <li>Access to <strong>all AI Employees</strong></li>
              <li><strong>All integrations</strong> (Slack, Trello, Google, and more)</li>
              <li><strong>Email support</strong> with 24-hour response time</li>
              <li><strong>Brain AI training</strong> - personalize AI responses with your business data</li>
            </ul>

            {/* Professional Plan */}
            <h3 className={`text-xl font-semibold mt-8 mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Professional Plan - $50/month (Most Popular)
            </h3>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Our <strong>most popular plan</strong>, designed for <strong>growing businesses</strong>. The Professional plan includes:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li><strong>3,000 credits per month</strong></li>
              <li>Access to <strong>all AI Employees</strong></li>
              <li><strong>All integrations</strong></li>
              <li><strong>Priority support</strong> with faster response times</li>
              <li><strong>Advanced Brain AI</strong> with more training capacity</li>
              <li><strong>Team collaboration</strong> features - invite team members</li>
              <li><strong>Analytics dashboard</strong> - track usage and performance</li>
            </ul>

            {/* Enterprise Plan */}
            <h3 className={`text-xl font-semibold mt-8 mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Enterprise Plan - $150/month
            </h3>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Built for <strong>large organizations</strong> with advanced needs. The Enterprise plan includes:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li><strong>Unlimited credits</strong> - no limits on AI interactions</li>
              <li>Access to <strong>all AI Employees</strong></li>
              <li><strong>All integrations</strong></li>
              <li><strong>Dedicated support</strong> with a personal account manager</li>
              <li><strong>Custom Brain AI training</strong> with unlimited data uploads</li>
              <li><strong>White-label option</strong> - brand SweetPilot as your own</li>
              <li><strong>Custom AI Employees</strong> - create employees tailored to your business</li>
              <li><strong>Full API access</strong> - integrate SweetPilot into your own applications</li>
            </ul>
            <p className={`mb-8 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              For custom enterprise solutions,{' '}
              <Link to="/contact" className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}>
                contact our sales team
              </Link>.
            </p>

            {/* Billing periods */}
            <h2 id="billing-periods" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Billing periods (how you're charged)
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              SweetPilot offers two billing options:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li><strong>Monthly billing</strong> - Pay month-to-month with no commitment. Cancel anytime.</li>
              <li><strong>Annual billing</strong> - Pay for a full year upfront and <strong>save 17%</strong> compared to monthly pricing.</li>
            </ul>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <strong>Annual pricing breakdown:</strong>
            </p>
            <ul className={`list-disc pl-6 mb-8 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>Pro: <strong>$250/year</strong> (save $50)</li>
              <li>Professional: <strong>$500/year</strong> (save $100)</li>
              <li>Enterprise: <strong>$1,500/year</strong> (save $300)</li>
            </ul>

            {/* Current pricing */}
            <h2 id="current-pricing" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Current pricing
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Here's a quick summary of our current pricing (monthly):
            </p>
            <div className={`overflow-x-auto mb-8 rounded-xl border ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
              <table className={`w-full text-left ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                <thead className={isDark ? 'bg-dark-800' : 'bg-slate-50'}>
                  <tr>
                    <th className="px-4 py-3 font-semibold">Plan</th>
                    <th className="px-4 py-3 font-semibold">Price</th>
                    <th className="px-4 py-3 font-semibold">Credits</th>
                    <th className="px-4 py-3 font-semibold">Best For</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className={isDark ? 'border-t border-white/5' : 'border-t border-slate-100'}>
                    <td className="px-4 py-3 font-medium">Free</td>
                    <td className="px-4 py-3">$0</td>
                    <td className="px-4 py-3">100/month</td>
                    <td className="px-4 py-3">Exploring</td>
                  </tr>
                  <tr className={isDark ? 'border-t border-white/5' : 'border-t border-slate-100'}>
                    <td className="px-4 py-3 font-medium">Pro</td>
                    <td className="px-4 py-3">$25/month</td>
                    <td className="px-4 py-3">1,500/month</td>
                    <td className="px-4 py-3">Freelancers</td>
                  </tr>
                  <tr className={isDark ? 'border-t border-white/5' : 'border-t border-slate-100'}>
                    <td className="px-4 py-3 font-medium">Professional</td>
                    <td className="px-4 py-3">$50/month</td>
                    <td className="px-4 py-3">3,000/month</td>
                    <td className="px-4 py-3">Growing teams</td>
                  </tr>
                  <tr className={isDark ? 'border-t border-white/5' : 'border-t border-slate-100'}>
                    <td className="px-4 py-3 font-medium">Enterprise</td>
                    <td className="px-4 py-3">$150/month</td>
                    <td className="px-4 py-3">Unlimited</td>
                    <td className="px-4 py-3">Large organizations</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className={`mb-8 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              View our full{' '}
              <Link to="/pricing" className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}>
                pricing page
              </Link>{' '}
              for a detailed feature comparison.
            </p>

            {/* How credits work */}
            <h2 id="how-credits-work" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              How credits work
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <strong>Credits</strong> are the currency used to interact with AI Employees. Here's how they work:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li><strong>Simple messages</strong> (short questions, quick tasks) use <strong>1 credit</strong></li>
              <li><strong>Complex tasks</strong> (long-form content, data analysis) use <strong>2-5 credits</strong></li>
              <li><strong>Power-ups</strong> (instant tasks like image generation) use <strong>5-10 credits</strong></li>
              <li><strong>Automations</strong> consume credits based on the actions performed</li>
            </ul>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <strong>What happens when you run out?</strong>
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>We'll notify you when you're <strong>running low on credits</strong></li>
              <li>You can <strong>purchase additional credits</strong> at any time</li>
              <li>Or <strong>upgrade to a higher plan</strong> for more monthly credits</li>
              <li>Unused credits <strong>do not roll over</strong> to the next month</li>
            </ul>
            <p className={`mb-8 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Learn more about{' '}
              <Link to="/doc/collections/account-billing" className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}>
                managing your credits and billing
              </Link>.
            </p>

            {/* Money-back guarantee */}
            <h2 id="money-back-guarantee" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Money-back guarantee (first 14 days)
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              We offer a <strong>14-day money-back guarantee</strong> on all paid plans. If you're not satisfied with SweetPilot for any reason:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>Contact our support team within <strong>14 days</strong> of your purchase</li>
              <li>We'll process a <strong>full refund</strong> - no questions asked</li>
              <li>Refunds are typically processed within <strong>5-7 business days</strong></li>
            </ul>
            <p className={`mb-8 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Read our complete{' '}
              <Link to="/refund-policy" className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}>
                refund policy
              </Link>{' '}
              for more details.
            </p>

            {/* More Information */}
            <h2 id="more-information" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              More Information
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <strong>Payment methods accepted:</strong>
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>All major <strong>credit cards</strong> (Visa, Mastercard, American Express)</li>
              <li><strong>PayPal</strong></li>
              <li><strong>Bank transfers</strong> (Enterprise plans only)</li>
            </ul>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <strong>Changing your plan:</strong>
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li><strong>Upgrading</strong> - Takes effect immediately, you'll be charged the prorated difference</li>
              <li><strong>Downgrading</strong> - Takes effect at your next billing cycle</li>
              <li><strong>Canceling</strong> - You'll retain access until the end of your billing period</li>
            </ul>
            <p className={`mb-8 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Have questions about pricing?{' '}
              <Link to="/help" className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}>
                Contact our support team
              </Link>{' '}
              or visit our{' '}
              <Link to="/pricing" className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}>
                pricing page
              </Link>.
            </p>

            {/* Divider */}
            <hr className={`my-12 ${isDark ? 'border-white/10' : 'border-slate-200'}`} />

            {/* Related Articles */}
            <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Related Articles
            </h2>
            <div className={`rounded-2xl border overflow-hidden ${
              isDark ? 'bg-dark-800/50 border-white/10' : 'bg-white border-slate-200'
            }`}>
              {relatedArticles.map((article, index) => (
                <Link
                  key={index}
                  to={article.href}
                  className={`flex items-center justify-between p-4 transition-colors ${
                    index !== relatedArticles.length - 1 ? (isDark ? 'border-b border-white/5' : 'border-b border-slate-100') : ''
                  } ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                >
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {article.title}
                  </span>
                  <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                </Link>
              ))}
            </div>

            {/* Feedback Section */}
            <div className={`mt-12 p-8 rounded-2xl text-center ${
              isDark ? 'bg-dark-800/50 border border-white/10' : 'bg-slate-50 border border-slate-200'
            }`}>
              <p className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Did this answer your question?
              </p>
              <div className="flex justify-center gap-4">
                <button className="text-3xl hover:scale-110 transition-transform" title="No">😞</button>
                <button className="text-3xl hover:scale-110 transition-transform" title="Somewhat">😐</button>
                <button className="text-3xl hover:scale-110 transition-transform" title="Yes">😃</button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Sidebar - Table of Contents */}
        <aside className="hidden lg:block w-56 shrink-0 ml-auto">
          <div className="sticky top-24 pl-6 border-l border-slate-200 dark:border-white/10">
            {/* Sidebar Title */}
            <h3 className={`text-base mb-4 ${
              isAtTop
                ? isDark ? 'text-white font-semibold' : 'text-slate-900 font-semibold'
                : isDark ? 'text-gray-400' : 'text-slate-600'
            }`}>
              Plans and pricing
            </h3>
            <nav className="space-y-2">
              {tableOfContents.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full text-left py-1.5 text-sm transition-colors ${
                    activeSection === item.id
                      ? isDark
                        ? 'text-white font-medium'
                        : 'text-slate-900 font-medium'
                      : isAtTop && index === 0
                        ? isDark
                          ? 'text-white font-medium'
                          : 'text-slate-900 font-medium'
                        : isDark
                          ? 'text-gray-500 hover:text-gray-300'
                          : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {item.title}
                </button>
              ))}
            </nav>
          </div>
        </aside>
      </div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-center mt-16"
      >
        <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
          isDark ? 'bg-purple-500/20' : 'bg-purple-100'
        }`}>
          <span className="text-2xl">💰</span>
        </div>
        <a
          href="mailto:billing@sweetpilot.com"
          className={`text-sm transition-colors ${
            isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          billing@sweetpilot.com
        </a>
      </motion.div>
      </StaticPageLayout>
    </>
  );
}
